import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'
import { useSettingsStore } from '@/store/settingsStore'
import { refreshAccessToken } from '@/api/tokenRefresh'
import { getOrCreateVisitorId, VISITOR_HEADER } from '@/lib/funnel'

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const authEndpointsToSkipRefresh = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/auth/password-reset/request',
  '/auth/password-reset/confirm',
]

function shouldSkipRefresh(url?: string) {
  return authEndpointsToSkipRefresh.some((endpoint) => url?.includes(endpoint))
}

function setAuthorizationHeader(config: InternalAxiosRequestConfig, accessToken: string) {
  config.headers.set('Authorization', `Bearer ${accessToken}`)
}

export const apiClient = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  const accessToken = useAuthStore.getState().accessToken

  if (accessToken) {
    setAuthorizationHeader(config, accessToken)
  }

  try {
    config.headers.set(VISITOR_HEADER, getOrCreateVisitorId())
  } catch {
    // ignore storage failures
  }

  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableRequestConfig | undefined

    if (
      error.response?.status === 401 &&
      config &&
      !config._retry &&
      !shouldSkipRefresh(config.url)
    ) {
      config._retry = true

      try {
        const accessToken = await refreshAccessToken()
        setAuthorizationHeader(config, accessToken)
        return apiClient(config)
      } catch {
        useAuthStore.getState().clearAuth()
        useSettingsStore.getState().clearSettings()
        if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
          window.location.href = '/'
        }
        return new Promise(() => {})
      }
    }

    return Promise.reject(error)
  }
)
